/**
 * Sistema de Geolocalização Global
 * Solicita permissão de localização e armazena coordenadas em localStorage
 */

class GeolocationManager {
  constructor() {
    this.storageKey = 'user_geolocation';
    this.expirationKey = 'user_geolocation_expiration';
    this.expirationHours = 24; // Expira após 24 horas
  }

  /**
   * Verifica se a geolocalização está disponível
   */
  isSupported() {
    return 'geolocation' in navigator;
  }

  /**
   * Verifica se os dados de localização ainda são válidos
   */
  isLocationValid() {
    const expiration = localStorage.getItem(this.expirationKey);
    if (!expiration) return false;
    
    const expirationDate = new Date(expiration);
    return new Date() < expirationDate;
  }

  /**
   * Obtém a localização armazenada
   */
  getStoredLocation() {
    if (!this.isLocationValid()) {
      this.clearLocation();
      return null;
    }

    const stored = localStorage.getItem(this.storageKey);
    if (!stored) return null;

    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Erro ao ler localização armazenada:', e);
      return null;
    }
  }

  /**
   * Armazena a localização
   */
  storeLocation(latitude, longitude) {
    const location = { latitude, longitude, timestamp: new Date().toISOString() };
    
    localStorage.setItem(this.storageKey, JSON.stringify(location));
    
    const expiration = new Date();
    expiration.setHours(expiration.getHours() + this.expirationHours);
    localStorage.setItem(this.expirationKey, expiration.toISOString());
    
    // Dispara evento customizado para notificar outras partes da aplicação
    window.dispatchEvent(new CustomEvent('geolocationUpdated', { 
      detail: location 
    }));
    
    return location;
  }

  /**
   * Limpa a localização armazenada
   */
  clearLocation() {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.expirationKey);
  }

  /**
   * Converte coordenadas em CEP usando API de geocoding reverso
   */
  async coordinatesToZipCode(latitude, longitude) {
    try {
      // Usando API do ViaCEP com geocoding reverso (alternativa: usar Google Maps API)
      // Como ViaCEP não tem geocoding reverso, vamos usar Nominatim (OpenStreetMap)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'Shopify-Store-Locator'
          }
        }
      );
      
      if (!response.ok) throw new Error('Erro ao buscar endereço');
      
      const data = await response.json();
      const zipCode = data.address?.postcode;
      
      if (zipCode) {
        return zipCode.replace(/\D/g, ''); // Remove caracteres não numéricos
      }
      
      return null;
    } catch (error) {
      console.error('Erro ao converter coordenadas em CEP:', error);
      return null;
    }
  }

  /**
   * Solicita permissão e obtém a localização atual
   */
  async requestLocation(options = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isSupported()) {
        reject(new Error('Geolocalização não é suportada pelo navegador'));
        return;
      }

      const defaultOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location = this.storeLocation(latitude, longitude);
          
          console.log('Localização obtida:', { latitude, longitude });
          resolve(location);
        },
        (error) => {
          console.error('Erro ao obter localização:', error.message);
          reject(error);
        },
        { ...defaultOptions, ...options }
      );
    });
  }

  /**
   * Obtém a localização (armazenada ou nova)
   */
  async getLocation(forceNew = false) {
    if (!forceNew) {
      const stored = this.getStoredLocation();
      if (stored) {
        console.log('Usando localização armazenada:', stored);
        return stored;
      }
    }

    return await this.requestLocation();
  }

  /**
   * Obtém o CEP baseado na localização atual
   */
  async getCurrentZipCode(forceNew = false) {
    try {
      const location = await this.getLocation(forceNew);
      const zipCode = await this.coordinatesToZipCode(location.latitude, location.longitude);
      
      if (zipCode) {
        // Armazena o CEP também
        localStorage.setItem('user_zipcode', zipCode);
        window.dispatchEvent(new CustomEvent('zipCodeDetected', { 
          detail: { zipCode, location } 
        }));
      }
      
      return zipCode;
    } catch (error) {
      console.error('Erro ao obter CEP:', error);
      return null;
    }
  }
}

// Instância global
window.geolocationManager = new GeolocationManager();

// Auto-inicialização (opcional - pode ser desabilitado via configuração)
document.addEventListener('DOMContentLoaded', () => {
  const autoRequest = document.documentElement.dataset.autoGeolocation === 'true';
  
  if (autoRequest) {
    // Verifica se já tem permissão antes de solicitar
    const stored = window.geolocationManager.getStoredLocation();
    if (!stored) {
      // Aguarda 2 segundos antes de solicitar para não ser intrusivo
      setTimeout(() => {
        window.geolocationManager.getLocation().catch(err => {
          console.log('Usuário negou permissão de localização ou erro:', err.message);
        });
      }, 2000);
    }
  }
});
